import { AxiosResponse } from "axios";
import { Car, UpdateCarQcInformation } from "features/cars/types";
import { defaultFilterQuery } from ".";
import api from "../apiService";
import {
	AppointmentsQuery,
	BaseQuery,
	CarQuery,
	ModelQuery,
	YearModelQuery,
} from "../queries";
import { API_VERSION } from "constants/api-versions";
import { ApiResponse } from "types";
import { CAR_TYPE } from "constants/constants";

export const getCarDetailAPI = async (id: string | number): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response: AxiosResponse = await api.get(`/dashboard/cars/${id}`);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getCarsApi = (query: CarQuery): Promise<any> =>
	new Promise(async (resolve, reject) => {
		const payload: CarQuery = { ...query };
		for(const field in payload) {
			if(payload[field as keyof CarQuery] === null) delete payload[field as keyof CarQuery];
		}
		let endpoint = "/dashboard/cars/filter";
		payload.skip = payload.skip * (payload.take ?? 0);
		endpoint += `?skip=${payload.skip}&take=${payload.take}`;
		api
			.post(endpoint, payload)
			.then(result => resolve(result.data))
			.catch(error => reject(error));
	});

export const getCarsViewsAndFavoritesAPI = async (
	ids: number[],
): Promise<void> =>
	new Promise(async (resolve, reject) => {
		try {
			const response: AxiosResponse = await api.get(
				"/dashboard/cars/views-favorites",
				{
					ids: ids.join(),
					version: API_VERSION.V_2023_05_16,
				},
			);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getCarModelsAPI = async (
	query: ModelQuery = defaultFilterQuery,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response: AxiosResponse = await api.get("/car-model", query);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const updateCarDetailAPI = async (
	id: string | number,
	car: Car,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const headerParams = { version: API_VERSION.V_2023_05_16 };
			const response: AxiosResponse = await api.put(
				`/dashboard/cars/${id}`,
				car,
				headerParams,
			);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const updateCarStatusAPI = async (
	id: string | number,
	body: any,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const headerParams = { version: API_VERSION.V_2023_05_16 };
			const response: AxiosResponse = await api.patch(
				`/dashboard/cars/${id}/status`,
				body,
				headerParams,
			);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});
	
export const updateRentalCarStatusAPI = async (
	id: string | number,
	body: any,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		const endpoint = `/dashboard/cars/rental/${id}/status`;
		try {
			const response: AxiosResponse = await api.patch(
				endpoint,
				body,
			);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getBookingForPublishCar = (id: number | string) : Promise<any> => 
	new Promise((resolve, reject) => {
		const endpoint = `/dashboard/cars/${id}/booked`;
		api.get(endpoint)
			.then(res => resolve(res.data))
			.catch(error => reject(error));
	});

export const getCarTestDrivesAPI = async (id: string | number): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response: AxiosResponse = await api.get(
				`/dashboard/test-drives/${id}`,
			);
			resolve(response.data.data);
		} catch (err) {
			reject(err);
		}
	});

export const getCarBrandsAPI = async (
	query: BaseQuery = defaultFilterQuery,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response: AxiosResponse = await api.get("/brands", query);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getCarAppointmentsApi = async (
	id: string | number | undefined,
	query: AppointmentsQuery,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const { skip, take } = query;
			const response = await api.get(`/dashboard/cars/${id}/appointments`, {
				...query,
				skip: skip * take,
			});
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const createCarsAPI = async (cars: Car[]): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response: AxiosResponse = await api.post("/dashboard/cars/", cars);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getListAvailableCars = async (ids: number[]): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const payload = { ids: ids.join(","), version: API_VERSION.V_2023_05_16 };
			const response: AxiosResponse = await api.get(
				"/dashboard/cars/available",
				payload,
			);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getCarQcInformationApi = async (
	id?: string,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response = await api.get(`/dashboard/car-qc-information/${id}`);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});
		
export const updateCarQcInformationApi = async (
	id?: string,
	body?: UpdateCarQcInformation,
): Promise<any> =>
	new Promise(async (resolve, reject) => {
		try {
			const response = await api.put(`/dashboard/car-qc-information/${id}`, body);
			resolve(response.data);
		} catch (err) {
			reject(err);
		}
	});

export const getMerchantRequestCarsAPI = (
	id: string, take?: number, skip?: number
): Promise<ApiResponse<Car[]>> =>
	new Promise(async (resolve, reject) => {
		const path = `/dashboard/merchant-requests/${id}/cars?skip=${skip ?? 0}&take=${take ?? 0}`;
		api
			.get<ApiResponse<Car[]>>(path, { language: "en" })
			.then(res => resolve(res.data))
			.catch(error => reject(error));
	});
				